import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GuestJoin() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleJoin = async () => {
    if (!name.trim()) {
      toast.error("What's your name?");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/sessions/${sessionId}/join`, {
        name: name.trim(),
      });

      const { participant } = response.data;
      
      sessionStorage.setItem("participantId", participant.id);
      sessionStorage.setItem("participantName", participant.name);
      sessionStorage.setItem("isHost", "false");
      
      // Go to lobby first
      navigate(`/lobby/${sessionId}`);
    } catch (error) {
      console.error("Failed to join session:", error);
      if (error.response?.status === 404) {
        toast.error("Session not found or expired");
        navigate("/");
      } else {
        toast.error("Couldn't join. Try again!");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleJoin();
    }
  };

  return (
    <main className="app-container">
      <div className="flex-1 flex flex-col px-6 py-8 justify-center">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-forkyeah-main mb-2">
            Fork<span className="text-forkyeah-primary">Yeah</span>
          </h1>
          <p className="text-forkyeah-muted font-semibold text-sm">
            You've been invited to decide where to eat
          </p>
        </motion.div>

        {/* Visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="flex justify-center mb-8"
        >
          <div className="w-20 h-20 rounded-full bg-forkyeah-accent/30 flex items-center justify-center">
            <Users className="w-10 h-10 text-forkyeah-primary" />
          </div>
        </motion.div>

        {/* Name Input */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <label className="text-xs font-bold text-forkyeah-muted uppercase tracking-wider block mb-2 text-center">
            What's your name?
          </label>
          <input
            data-testid="guest-name-input"
            type="text"
            placeholder="First name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyPress={handleKeyPress}
            className="input-field text-center"
            autoFocus
          />
        </motion.div>

        {/* Continue Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <button
            data-testid="join-session-btn"
            onClick={handleJoin}
            disabled={loading || !name.trim()}
            className="btn-primary gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Joining...
              </>
            ) : (
              <>
                Join session
                <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
              </>
            )}
          </button>
        </motion.div>

        {/* Trust cue */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-xs text-forkyeah-muted/70 mt-6 font-medium"
        >
          No signup needed. Just swipe and match.
        </motion.p>
      </div>
    </main>
  );
}
